import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { deskApi } from "@/lib/deskApi";
import { sessionLabel } from "@/lib/format";
import { QueryBoundary, retryTransient } from "@/lib/queryState";
import { skewUsable } from "@/lib/checkpointState";
import { Empty } from "@/components/widgets";
import { PageHead, Panel, Button, Icon, DecisionBadge, DECISIONS, Switch, normDecision, fmt } from "@/sentinel";

const abbr = (d) => ({ "ENTER": "ENT", "ENTER PRE-OPEN": "ENT-PO", "WAIT": "WAIT", "WAIT FOR OPEN": "WAIT-O", "NO TRADE": "NT", "PROVISIONAL": "PROV" }[d] || "—");

// Historical decisions are all muted (one loud thing per screen). The heatmap reads by glyph + abbreviation,
// with a graphite weight ramp: ENTER strongest, WAIT middle, NO TRADE and PROVISIONAL quiet.
const cellTone = (d) => (d === "ENTER" || d === "ENTER PRE-OPEN"
  ? { color: "var(--text-primary)", fontWeight: 600 }
  : d === "WAIT" || d === "WAIT FOR OPEN" ? { color: "var(--text-primary)" } : { color: "var(--text-tertiary)" });

// Put-wing skew across Runs 1-6 (live sessions only): taller / stronger = fatter downside wing.
function SkewStrip({ skew, date }) {
  const runs = [1, 2, 3, 4, 5, 6];
  // A suspect or missing reading is withheld, never drawn as a wing percentage.
  const vals = runs.map((n) => (skewUsable(skew?.[n]) ? skew[n].put_wing_pct ?? null : null));
  const suspect = runs.some((n) => skew?.[n]?.suspect);
  if (!vals.some((v) => v != null)) return <span className="sn-faint" title={suspect ? "Skew withheld: suspect reading" : undefined}>{suspect ? "withheld" : "—"}</span>;
  const max = Math.max(40, ...vals.filter((v) => v != null).map((v) => Math.abs(v)));
  const first = vals.find((v) => v != null);
  const lastV = [...vals].reverse().find((v) => v != null);
  const delta = lastV - first;
  const seq = (v) => `var(--viz-seq-${Math.min(5, Math.max(2, Math.ceil((Math.abs(v) / max) * 5)))})`;
  const sgn = (v, d) => (v > 0 ? "+" : v < 0 ? "−" : "") + Math.abs(v).toFixed(d);
  return (
    <div className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "nowrap" }} data-testid={`history-skew-${date}`}
      title={runs.map((n) => `R${n}: ${vals[n - 1] == null ? "—" : sgn(vals[n - 1], 1) + "%"}`).join(" · ") + " (put wing vs ATM IV)"}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 18 }} aria-hidden="true">
        {runs.map((n) => {
          const v = vals[n - 1];
          const h = v == null ? 2 : Math.max(2, Math.round((Math.abs(v) / max) * 18));
          return <span key={n} data-testid={`history-skew-${date}-r${n}`} style={{ width: 5, height: h, borderRadius: 1, display: "inline-block",
            background: v == null ? "var(--border-strong)" : seq(v) }} />;
        })}
      </div>
      <span className="sn-num sn-small sn-muted">
        {sgn(lastV, 0)}%{vals.filter((v) => v != null).length > 1 ? ` (${sgn(delta, 0)} pts)` : ""}
      </span>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="sn-field" style={{ minWidth: 200 }}>
      <span className="sn-field-label">{label}</span>
      <span className="sn-input-wrap">
        <select value={value} onChange={(e) => onChange(e.target.value)} className="sn-input" data-testid={testId} style={{ cursor: "pointer" }}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </span>
    </label>
  );
}

export default function History() {
  const [includeDemo, setIncludeDemo] = useState(false);
  const query = useQuery({ queryKey: ["history", includeDemo], queryFn: () => deskApi.history({ limit: 80, includeDemo }), retry: retryTransient });
  const [fScenario, setFScenario] = useState("");
  const [fPath, setFPath] = useState("");

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="history-page">
      <PageHead
        crumb={["Evidence", "History"]}
        title="Decision history"
        context={includeDemo ? "All recorded sessions, including demo and fixture sessions" : "Live sessions only · the decision sealed at each run"}
        actions={<Switch label="Show demo/fixture sessions" checked={includeDemo} onChange={setIncludeDemo} data-testid="history-include-demo" />}
      />
      <QueryBoundary query={query} loading="page" what="the session history" errorTestId="history-error"
        isEmpty={(d) => !visibleSessions(d, includeDemo).length}
        empty={{ icon: "History", title: includeDemo ? "No sessions recorded yet" : "No live sessions recorded yet", body: includeDemo ? "Sessions appear here once a run is sealed." : "Turn on demo/fixture sessions to see test data.", testId: "history-empty" }}>
        {(data) => (
          <HistoryBody data={data} sessions={visibleSessions(data, includeDemo)} includeDemo={includeDemo} fScenario={fScenario} setFScenario={setFScenario} fPath={fPath} setFPath={setFPath} />
        )}
      </QueryBoundary>
    </div>
  );
}

/** Older backends ignore include_demo, so live-only is also enforced here. */
function visibleSessions(data, includeDemo) {
  const all = data?.sessions || [];
  return includeDemo ? all : all.filter((s) => s.provider === "polygon");
}

const LEGEND = [
  ["ENTER", "ENTER"], ["ENTER PRE-OPEN", "ENTER PRE-OPEN"], ["WAIT", "WAIT"], ["WAIT FOR OPEN", "WAIT FOR OPEN"],
  ["NO TRADE", "NO TRADE"], ["PROVISIONAL", "PROVISIONAL (pre-market build)"],
];

function HistoryBody({ data, sessions, includeDemo, fScenario, setFScenario, fPath, setFPath }) {
  // Run 6 is the post-close review: it carries no decision, so it has no column here.
  const runOrder = (data?.run_order || [1, 2, 3, 4, 5]).filter((n) => n !== 6);
  // Live-only mode holds one kind of session, so the scenario filter and column would read the same on every row.
  const showScenario = !!includeDemo;
  const scenarios = [...new Set(sessions.map(sessionLabel))].sort();
  const paths = [...new Set(sessions.map((s) => s.final_path_state).filter(Boolean))].sort();
  const filtered = sessions.filter((s) =>
    (!showScenario || !fScenario || sessionLabel(s) === fScenario) && (!fPath || s.final_path_state === fPath));
  const rev = [...filtered].reverse(); // most recent first for the table

  return (
    <>
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="history-legend">
        Cells show the sealed decision by glyph and code:{" "}
        {LEGEND.map(([d, label], i) => (
          <span key={d}><span className="sn-num" style={cellTone(d)}><Icon name={DECISIONS[d].icon} size={12} /> {abbr(d)}</span> {label}{i < LEGEND.length - 1 ? " · " : ". "}</span>
        ))}
        A <span className="sn-num">·L</span> suffix means the run was sealed late.{" "}
        Skew R1→6 is the put-wing IV premium over ATM, in percent; the bracket is the change from R1 to the last run, in percentage points.
      </p>

      <div className="sn-row" style={{ gap: "var(--space-3)", alignItems: "flex-end" }} data-testid="history-filters">
        {showScenario && (
          <FilterSelect label="Regime" value={fScenario} onChange={setFScenario} testId="history-filter-scenario"
            options={[["", "All scenarios"], ...scenarios.map((s) => [s, s])]} />
        )}
        <FilterSelect label="Path state" value={fPath} onChange={setFPath} testId="history-filter-path"
          options={[["", "All paths"], ...paths.map((p) => [p, p])]} />
        {((showScenario && fScenario) || fPath) && (
          <Button variant="ghost" icon="X" onClick={() => { setFScenario(""); setFPath(""); }} data-testid="history-filter-clear">Clear filters</Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Empty testId="history-filtered-empty" icon="Filter" title="No matching sessions">No sessions match these filters.</Empty>
      ) : (
        <>
          <Panel title="Sessions" icon="History" flush meta={<span>{filtered.length} of {sessions.length} sessions · newest first</span>}>
            <div style={{ overflowX: "auto" }}>
              <table className="sn-table" data-testid="history-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Session</th>
                    {showScenario && <th>Scenario</th>}
                    {runOrder.map((n) => <th key={n} style={{ textAlign: "center" }}>R{n}</th>)}
                    <th>Final</th>
                    <th className="r">Conf</th>
                    <th className="r">Exp range</th>
                    <th>Path</th>
                    <th title="Put-wing IV skew vs ATM across the runs; suspect readings are withheld">Skew R1→6</th>
                  </tr>
                </thead>
                <tbody>
                  {rev.map((s) => (
                    <tr key={s.session_date} data-testid={`history-row-${s.session_date}`}>
                      <td className="id sticky-col">{s.session_date}</td>
                      {showScenario && <td className="sn-small" data-testid={`history-provider-${s.session_date}`}>{sessionLabel(s)}</td>}
                      {runOrder.map((n) => {
                        const r = s.runs?.[n];
                        const d = r ? normDecision(r.decision_label) : null;
                        return (
                          <td key={n} className="sn-num sn-small" style={{ textAlign: "center", ...(d ? cellTone(d) : { color: "var(--text-tertiary)" }) }}
                            data-testid={`history-cell-${s.session_date}-${n}`}
                            title={r ? `${r.decision_label}${r.blocking_rule ? " · block " + r.blocking_rule : ""}${r.late ? " · LATE" : ""}` : "not run"}>
                            {r ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{DECISIONS[d] && <Icon name={DECISIONS[d].icon} size={12} />}{abbr(d)}{r.late ? "·L" : ""}</span> : "·"}
                          </td>
                        );
                      })}
                      <td>{s.final_decision ? <DecisionBadge decision={s.final_decision} size="compact" muted /> : <span className="sn-faint">—</span>}</td>
                      <td className="num">{s.final_confidence ?? "—"}</td>
                      <td className="num">{fmt(s.final_expected_range, "pts")}</td>
                      <td className="sn-small">{s.final_path_state || "—"}</td>
                      <td><SkewStrip skew={s.skew} date={s.session_date} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}
